import React from "react";
import { makeStyles } from "@mui/styles";
import RadioGroup from "@mui/material/RadioGroup";
import Radio from "@mui/material/Radio";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormLabel from "@mui/material/FormLabel";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Grid from "@mui/material/Grid";
import Tooltip from "@mui/material/Tooltip";
import InfoIcon from "@mui/icons-material/Info";
import { blue } from "@mui/material/colors";
import { DateTimePicker } from "@mui/x-date-pickers";
import TimezoneSelect from 'react-timezone-select'
import moment from "moment-timezone";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
  },
  formControl: {
    margin: theme.spacing(3),
  },
  group: {
    margin: `${theme.spacing(1)}px 0`,
  },
  selectInput: {
    backgroundColor: "transparent",
  },
  formControl: {
    flexDirection: "column",
    margin: theme.spacing(3),
    width: "calc(100% - 48px)",
  },
  timezoneContainer: {
    marginTop: theme.spacing(1),
    zIndex: 10,
  },
}));

const WindowLabel = (props) => {
  const { showWarning = true } = props;
  return (
    <FormLabel
      component="legend"
      style={{
        fontSize: 14,
        fontStyle: "italic",
        marginTop: 12,
      }}
    >
      {!showWarning ? (
        "Window"
      ) : (
        <>
          Window (maximum)
          <Tooltip title="Based upon the event rates, you may not be able to use the full window requested.">
            <InfoIcon fontSize="small" style={{ color: blue[500] }} />
          </Tooltip>
        </>
      )}
    </FormLabel>
  );
};

const TimeDurationField = (props) => {
  const { timeframe, fieldId, errors, setOptions, validations, validate } =
    props;
  return (
    <TextField
      variant="standard"
      error={errors["timeDurationError"]}
      id={fieldId}
      //label="Length"
      value={timeframe["length.value"]}
      onChange={(event) => {
        setOptions({
          timeframe: {
            ...timeframe,
            "length.value": event.target.value,
          },
        });
        if (validate) {
          validations(event, timeframe, "number");
        }
      }}
      type="number"
      InputLabelProps={{ shrink: true }}
      margin="normal"
      style={{ marginLeft: 6, marginTop: 8, width: "10%" }} />
  );
};

const TimeUnitsField = (props) => {
  const classes = useStyles();
  const { timeframe, fieldId, setOptions, validations, validate } = props;
  return (
    <TextField
      variant="standard"
      id={fieldId}
      select
      //label="Units"
      value={timeframe["length.units"]}
      onChange={(event) => {
        setOptions({
          timeframe: {
            ...timeframe,
            "length.units": event.target.value,
          },
        });
        if (validate) {
          validations(event, timeframe, "units");
        }
      }}
      margin="normal"
      //variant="filled"
      style={{ marginLeft: 6, marginTop: 8, width: "15%" }}
      SelectProps={{
        className: classes.selectInput,
      }}>
      {!validate && (
        <MenuItem key="days" value="day">
          Days
        </MenuItem>
      )}
      <MenuItem key="weeks" value="week">
        Weeks
      </MenuItem>
      <MenuItem key="hours" value="month">
        Months
      </MenuItem>
    </TextField>
  );
};

const TimeframeWithTZOption = (props) => {
  const classes = useStyles();

  const convertToMinutes = (units) => {
    if (units === "minutes") {
      return 1;
    } else if (units === "hours") {
      return 60;
    } else if (units === "days") {
      return 60 * 24;
    }
  };

  const errorMessages = {
    timeDurationError: {
      minutes: "Please enter a value between 1 and 1440 minute(s)",
      hours: "Please enter a value between 1 and 24 hour(s)",
    },
  };

  const errorMessage = (units) => {
    let errorFound = false;
    for (let error in props.errors) {
      if (error.includes("time") && !errorFound) {
        errorFound = true;
        return (
          <p style={{ color: "red", fontSize: "10px" }}>
            {errorMessages[error][units]}
          </p>
        );
      }
    }
  };

  const validations = (event, timeframe, type) => {
    let id;
    let units;
    let value;

    if (type === "units") {
      id = "timeDurationError";
      units = event.target.value;
      value = timeframe["length.value"];
    } else if (type === "number") {
      id = "timeDurationError";
      units = timeframe["length.units"];
      value = event.target.value;
    }
    let valueAdjustedtoMinutes = convertToMinutes(units) * value;
    if (valueAdjustedtoMinutes <= 1440 && valueAdjustedtoMinutes >= 0) {
      props.noErrorDetected(id);
    } else {
      props.errorDetected(id);
    }
  };

  const timeframe = props.options.timeframe;
  const timezone = props.options.timezone;

  return (
    <FormControl variant="standard" style={{ width: "calc(100% - 48px)", margin: 24 }}>
      <FormLabel component="legend">Time Frame</FormLabel>
      { !(props.options?.hide_type_toggle ?? false ) && (
      <RadioGroup
        aria-label="Timeframe Type"
        name="timeframe-type"
        value={timeframe.type}
        row={true}
        onChange={(event) => {
          const value = event.target.value;
          let newOptions = { ...timeframe, type: value };
          if (value === "fixed" && timeframe.start == null) {
            newOptions.start = moment();
          }
          props.setOptions({ timeframe: newOptions });
        }}
      >
        <FormControlLabel value="moving" control={<Radio />} label="Moving" />
        <FormControlLabel value="fixed" control={<Radio />} label="Fixed" />
      </RadioGroup>
      )}
      {timeframe.type === "fixed" && (
        <div style={{ float: "right" }}>
          <Grid container direction="row" spacing={1}>
            <Grid item xs="4">
              <DateTimePicker
                key={timezone || "local"}
                error={false}
                id="starttime"
                label="Start"
                timeSteps={{hours: 1, minutes: 1}}
                value={
                  timezone
                    ? moment.tz(timeframe.start, timezone)
                    : timeframe.start
                }
                onChange={(dt) => {
                  props.setOptions({
                    timeframe: { ...timeframe, start: dt.valueOf() },
                  });
                }}
                slotProps={{ textField: { variant: "standard" } }}
                style={{ width: "100%", maxWidth: "100%", margin: 6 }}
              />
            </Grid>
            <Grid item xs="4">
              <DateTimePicker
                  key={timezone || "local"}
                  error={false}
                  id="endtime"
                  label="End"
                  timeSteps={{hours: 1, minutes: 1}}
                  value={
                    timezone
                      ? moment.tz(timeframe.end, timezone)
                      : timeframe.end
                  }
                  onChange={(dt) => {
                    props.setOptions({
                      timeframe: { ...timeframe, end: dt.valueOf() },
                    });
                  }}
                  slotProps={{ textField: { variant: "standard" } }}
                  style={{ width: "100%", maxWidth: "100%", margin: 6 }}
                />
            </Grid>
            <Grid item xs="4">
            <FormLabel component="legend">Timezone</FormLabel>
                <div className={classes.timezoneContainer}>
                  <TimezoneSelect
                    placeholder="Default to current timezone"
                    isClearable
                    value={timezone ?? {}}
                    onChange={(tz) => {
                      props.setOptions({
                        timezone: tz?.value ?? tz,
                      });
                    }}
                  />
                </div>
            </Grid>
          </Grid>
        </div>
      )}
      {timeframe.type === "moving" && (
        <FormControl variant="standard">
          
            <WindowLabel showWarning={props.showWarning} />
            <FormControl variant="standard" fullWidth={true} style={{ flexDirection: "row" }}>

                <TimeDurationField
                  timeframe={timeframe}
                  fieldId={"select-moving-length"}
                  errors={props.errors}
                  setOptions={props.setOptions}
                  validate={props.validate}
                  validations={validations}
                />
                <TimeUnitsField
                  timeframe={timeframe}
                  fieldId={"select-moving-units"}
                  setOptions={props.setOptions}
                  validate={props.validate}
                  validations={validations}
                />
                {errorMessage(timeframe["length.units"])}
            <FormLabel component="legend">Timezone</FormLabel>
            <div className={classes.timezoneContainer}>
              <TimezoneSelect
                placeholder="Default to current timezone"
                isClearable
                value={timezone ?? {}}
                onChange={(tz) => {
                  props.setOptions({
                    timezone: tz?.value ?? tz,
                  });
                }}
              />
            </div>
          
            </FormControl>
           </FormControl>
      )}
    </FormControl>
  );
};

export default TimeframeWithTZOption;
